import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Sidebar } from '@/components/layout/sidebar'
import { Header } from '@/components/layout/header'
import { LegalBannerSlot } from '@/components/legal'
import { LegalAcceptanceProvider } from '@/providers/legal-acceptance-provider'
import { SidebarProvider } from '@/providers/sidebar-provider'
import { LimitReachedDialogProvider } from '@/components/subscription/limit-reached-dialog'
import { useSidebar } from '@/hooks/use-sidebar'
import { cn } from '@/lib/utils'
import { TourCompanion } from '@/components/tour'
import { useTourStore } from '@/stores/use-tour-store'
import type { TourStep } from '@/stores/use-tour-store'

const TOUR_ROUTES: Record<TourStep, string | null> = {
  'create-admin': '/usuarios',
  'ask-financial': null, // stay on current page
  'show-financial': '/configuracoes',
  'show-students': '/alunos',
  'show-plans': '/planos',
  'show-calendar': '/calendario',
  completed: '/',
}

const STEP_HIGHLIGHTS: Record<TourStep, string[]> = {
  'create-admin': ['create-user-btn'],
  'ask-financial': [],
  'show-financial': ['sidebar-configuracoes', 'tab-financeiro'],
  'show-students': ['sidebar-alunos', 'create-student-btn'],
  'show-plans': ['sidebar-planos', 'create-plan-btn'],
  'show-calendar': ['sidebar-calendario', 'create-event-btn'],
  completed: [],
}

function AppContent() {
  const { isCollapsed, isMobile } = useSidebar()
  const currentStep = useTourStore((s) => s.currentStep)
  const isActive = useTourStore((s) => s.isActive)
  const setHighlights = useTourStore((s) => s.setHighlights)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isActive) return

    const target = TOUR_ROUTES[currentStep]

    if (target && target !== location.pathname) {
      navigate(target)
    }

    setHighlights(STEP_HIGHLIGHTS[currentStep] || [])
  }, [currentStep, isActive, navigate, location.pathname, setHighlights])

  return (
    <LimitReachedDialogProvider>
      <div className='h-screen overflow-hidden bg-background'>
        <Sidebar />
        <div
          className={cn(
            'flex h-screen flex-col overflow-hidden transition-[margin] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
            isMobile ? 'ml-0' : isCollapsed ? 'ml-16' : 'ml-64',
          )}
        >
          <Header />
          <LegalBannerSlot />
          <main className='relative flex-1 overflow-hidden'>
            <div className='main-scroll-area absolute inset-0 overflow-y-auto px-8 pt-8 pb-4'>
              <Outlet />
            </div>
          </main>
        </div>
        <TourCompanion />
      </div>
    </LimitReachedDialogProvider>
  )
}

export function AppLayout() {
  return (
    <LegalAcceptanceProvider>
      <SidebarProvider>
        <AppContent />
      </SidebarProvider>
    </LegalAcceptanceProvider>
  )
}
